import { createSlice } from "@reduxjs/toolkit";
import { genericStringSplitter } from "../../util/stringUtils";
import {
  QueryBuilders,
  ActionableQuery,
  QueryableObject,
  parseQuery,
  ValueParentQuery,
} from "../../from-server/asin-central/search";
import { getDomainFromUrl } from "../../util/urlUtils";
const { or, and, range, filter, fuzzy, val, all } = QueryBuilders;

// Lots of queries we can legally parse out of the url are not representable with
// the search side bar UI.  Instead of throwing, we're noting that these queries
// can't be represented in the UI and executing them anyway.  This allows sophisticated
// users to make complex queries and send them to users as links.
type Unrepresentable = "unrepresentable";

const UNREPRESENTABLE_AND_BYPASS = ["asinType"];

const separator = ",";

export type Range = {
  lower: Date;
  upper: Date;
};

export type ClientQuery = ActionableQuery<QueryableObject>;

// the keys are the attribute name, the values are queries for that attribute along with the UI state
export type UIFilters = {
  [key: string]: { value: string | Range }; //query: ClientQuery;
};

// PropertyFilter and PropertyFilterGroup types allow to build nested queries using the UI
export type PropertyFilter = {
  type: "PropertyFilter";
  operator: "filter" | "fuzzy";
  value: any;
  propertyKey: string;
};
export type PropertyFilterGroup = {
  type: "PropertyFilterGroup";
  filters: Array<PropertyFilterGroup | PropertyFilter>;
  operation: "and" | "or";
};

const DEFAULT_PROPERTY_FILTER_GROUP_VALUE: PropertyFilterGroup = {
  type: "PropertyFilterGroup",
  filters: [],
  operation: "and",
};

const LOW_DATE = new Date("1000-01-01");
const HIGH_DATE = new Date("9999-01-01");

function stringArrayToQuery(
  attribute: string,
  strs: string[]
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => filter(val(attribute, s.trim())));
  return or(eachValue[0], ...eachValue.slice(1));
}

function stringArrayToQueryWithOp(
  attribute: string,
  strs: string[],
  operator: "filter" | "fuzzy"
): ClientQuery | undefined {
  if (strs.length === 0) {
    return undefined;
  }
  const eachValue = strs.map((s) => {
    switch (operator) {
      case "filter":
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
      case "fuzzy":
        return fuzzy(val(attribute, sanitizeInputFilters(s.trim())));
      default:
        return filter(val(attribute, sanitizeInputFilters(s.trim())));
    }
  });
  return or(eachValue[0], ...eachValue.slice(1));
}

export function stringToQuery(
  attribute: string,
  str?: string | string[]
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQuery(attribute, str);
  }
  if (str.indexOf(separator)) {
    return stringArrayToQuery(attribute, str.split(separator));
  }
  return filter(val(attribute, str.trim()));
}

export function stringToQueryWithOp(
  attribute: string,
  str?: string | string[],
  operator: "filter" | "fuzzy" = "filter"
): ClientQuery | undefined {
  if (typeof str !== "string" || str === "") {
    return undefined;
  }
  if (Array.isArray(str)) {
    return stringArrayToQueryWithOp(attribute, str, operator);
  }
  if (str.indexOf(separator) > -1) {
    return stringArrayToQueryWithOp(attribute, str.split(separator), operator);
  }
  switch (operator) {
    case "filter":
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
    case "fuzzy":
      return fuzzy(val(attribute, sanitizeInputFilters(str.trim())));
    default:
      return filter(val(attribute, sanitizeInputFilters(str.trim())));
  }
}

export function datesToQuery(
  attribute: string,
  rangeState: Range
): ClientQuery | undefined {
  if (!rangeState.lower && !rangeState.upper) {
    return undefined;
  }
  const low = rangeState.lower || LOW_DATE;
  const high = rangeState.upper || HIGH_DATE;
  return range(val(attribute, low), val(attribute, high));
}

export const defaultQueryState: QueryState = {
  unrepresentable: false,
  queryMode: "UIFilter",
  queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
  queryPropertyFiltersForceRehydrate: false,
  activePropertyFilters: {},
  activePropertyFiltersVisibilityEnabled: false,
  queryParameters: {},
  queryString: all().toString(),
  isBaselineQuery: false,
};

function sumPropertyFilterQueries(query: PropertyFilterGroup): ClientQuery {
  if (!query || Object.keys(query).length === 0) return all();
  const subQueries: ClientQuery[] = [];
  query?.filters.forEach(
    (customFilter: PropertyFilterGroup | PropertyFilter) => {
      if (customFilter.type === "PropertyFilterGroup") {
        subQueries.push(sumPropertyFilterQueries(customFilter));
      }
      if (customFilter.type === "PropertyFilter") {
        if (customFilter.propertyKey === "asin") {
          const value = genericStringSplitter(
            customFilter.value as string
          ).join(separator);
          const clientQuery = stringToQueryWithOp(
            customFilter.propertyKey,
            value,
            customFilter.operator
          );
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        } else {
          const clientQuery =
            typeof customFilter.value === "string"
              ? stringToQueryWithOp(
                  customFilter.propertyKey,
                  customFilter.value,
                  customFilter.operator
                )
              : datesToQuery(customFilter.propertyKey, customFilter.value);
          if (clientQuery) {
            subQueries.push(clientQuery);
          }
        }
      }
    }
  );
  if (subQueries.length === 0) return all();
  return query.operation === "or"
    ? or(subQueries[0], ...subQueries.slice(1))
    : and(subQueries[0], ...subQueries.slice(1));
}

function sumQueries(filters: UIFilters): ClientQuery {
  const subQueries = Object.entries(filters)
    .map(([attributeName, value]) => {
      if (attributeName === "asin") {
        // Allow a poorly formatted list of ASIN's similar to SelectionCentral (see LEAP-7186).
        value.value = genericStringSplitter(value.value as string).join(
          separator
        );
      }
      if (attributeName === "asinType" && typeof value.value === "string") {
        // The UI ASIN's type filter groups types by party affiliation.
        // Most ASIN types have a one-to-one relationship with their party attribute or are party-agnostic.
        // However, the "Non MFA (1p)" and "Non MFA (2p)" ASIN types are special cases, as both of them use the same ASIN type key "non_mfa" and they are not party agnostic
        // To allow consistent filtering, we need to introduce two virtual keys in the UI ASIN's type filter: "1p_non_mfa" and "2p_non_mfa".
        // These virtual keys will be processed internally by joining the asinType and asinParty, enabling us to filter both "Non MFA (1p)" and "Non MFA (2p)" ASIN types simultaneously.
        const defaultSubQueries =
          stringToQuery(attributeName, value.value)?.getSubQueries() ?? [];
        const nonMFASubQueries = [];
        if (value.value.indexOf("1p_non_mfa") !== -1) {
          nonMFASubQueries.push(
            and(
              filter(val("asinType", "non_mfa")),
              filter(val("asinParty", "1p"))
            )
          );
        }
        if (value.value.indexOf("2p_non_mfa") !== -1) {
          nonMFASubQueries.push(
            and(
              filter(val("asinType", "non_mfa")),
              filter(val("asinParty", "2p"))
            )
          );
        }
        const asinTypeSubQueries = [...defaultSubQueries, ...nonMFASubQueries];
        if (asinTypeSubQueries.length === 0) return undefined;
        return or(asinTypeSubQueries[0], ...asinTypeSubQueries.slice(1));
      }
      if (value !== undefined && value.value !== undefined) {
        return typeof value?.value === "string"
          ? stringToQuery(attributeName, value.value)
          : datesToQuery(attributeName, value.value);
      }
      return undefined;
    })
    .filter((q) => q !== undefined) as ClientQuery[];
  return subQueries.length > 0
    ? and(subQueries[0], ...subQueries.slice(1))
    : all();
}

function getAttributeName(sq: ClientQuery): string {
  if (sq.type === "or" || sq.type === "and") {
    return getAttributeName(sq.getSubQueries()[0]);
  }
  return (sq as ValueParentQuery<any>).getValues()[0].attribute;
}

function getValue(sq: ClientQuery): string | Range {
  if (sq.type === "or") {
    return sq
      .getSubQueries()
      .map((q) => getValue(q))
      .filter((value) => value !== "")
      .join(", ");
  }
  if (
    sq.type === "and" &&
    UNREPRESENTABLE_AND_BYPASS.includes(getAttributeName(sq))
  )
    return "";
  if (sq.type === "range") {
    const values = (sq as ValueParentQuery<any>).getValues();
    return {
      upper: values[1].value as Date,
      lower: values[0].value as Date,
    };
  }
  return (sq as ValueParentQuery<any>).getValues()[0].value as string;
}

function removePropertyFilter(
  propertyKey: string,
  propertyFilterGroup: PropertyFilterGroup
): PropertyFilterGroup {
  const filters: (PropertyFilter | PropertyFilterGroup)[] = [];
  if (propertyFilterGroup.filters.length > 0) {
    propertyFilterGroup.filters?.forEach((f) => {
      if (f.type === "PropertyFilter" && f.propertyKey !== propertyKey) {
        filters.push(f);
      }
      if (f.type === "PropertyFilterGroup") {
        const subFilter = removePropertyFilter(propertyKey, f);
        if (subFilter.filters.length > 0) {
          filters.push(subFilter);
        }
      }
    });
  }
  propertyFilterGroup.filters = filters;
  return propertyFilterGroup;
}

function spreadPropertyFilterQueries(
  query: ClientQuery,
  depth = 0
): PropertyFilterGroup | Unrepresentable {
  const subQueries: Array<PropertyFilterGroup | PropertyFilter> = [];
  if (depth === 0) {
    if (query.type === "all") {
      return DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
    }
    if (query.type !== "and") {
      // Property filter component can only represent queries where the root is an AND
      return "unrepresentable";
    }
    if (query.getSubQueries().length > 2) {
      // Root element must have at most two sub queries (ASIN filter and PropertyFilter queries)
      return "unrepresentable";
    }
  }
  if (depth > 2) {
    // Property filter support at most root level + two nested levels
    return "unrepresentable";
  }
  for (let subQuery of query.getSubQueries()) {
    if (subQuery.type === "and" || subQuery.type === "or") {
      if (depth === 0) {
        // Process multiple ASINs filter if apply
        if (subQuery.type === "or" && getAttributeName(subQuery) === "asin") {
          const value = getValue(subQuery);
          subQueries.push({
            propertyKey: "asin",
            value: value ?? "",
            operator: "filter",
            type: "PropertyFilter",
          });
          continue;
        }
      }
      const propertyFilterGroup = spreadPropertyFilterQueries(
        subQuery,
        depth + 1
      );
      if (propertyFilterGroup === "unrepresentable") {
        return "unrepresentable";
      }
      if (propertyFilterGroup?.filters?.length > 0) {
        subQueries.push(propertyFilterGroup);
      }
    } else {
      // Nested ASIN filters used to be rejected as "unrepresentable" here
      // because the Legacy ASIN textarea always serialized ASIN as a
      // top-level `PropertyFilter`. The App Shell FilterBar exposes ASIN
      // as a first-class property in the horizontal `PropertyFilter`
      // editor, which serializes multi-value ASIN input as a nested
      // `(or (filter 'asin':'X') (filter 'asin':'Y'))` inside the
      // attribute group. The parser now accepts that shape so shared
      // links round-trip and render as separate ASIN tokens joined by OR
      // in the property filter chip row.
      const attribute = getAttributeName(subQuery);
      const value = getValue(subQuery);
      subQueries.push({
        propertyKey: attribute,
        value: value ?? "",
        operator: subQuery?.type ?? "filter",
        type: "PropertyFilter",
      } as PropertyFilter);
    }
  }
  return {
    type: "PropertyFilterGroup",
    filters: subQueries,
    operation: query?.type === "or" ? "or" : "and",
  };
}

function spreadQueries(query: ClientQuery): UIFilters | Unrepresentable {
  if (query.type === "all") {
    return {};
  }
  // the search side bar component can only represent queries where the root is an AND
  if (query.type !== "and") {
    return "unrepresentable";
  }
  const subQueries = query.getSubQueries();
  const uiLegalSecondGenNodes = new Set(["or", "range", "filter", "fuzzy"]);
  if (
    subQueries.filter((sq) => !uiLegalSecondGenNodes.has(sq.type)).length > 0
  ) {
    return "unrepresentable";
  }
  if (subQueries.length < 1) {
    return "unrepresentable";
  }
  return subQueries.reduce((uiFilters: UIFilters, sq) => {
    uiFilters[getAttributeName(sq)] = {
      value: getValue(sq),
    };
    return uiFilters;
  }, {});
}

function parsePropertyFilterQueryClientSide(
  query: string
): PropertyFilterGroup | Unrepresentable {
  return spreadPropertyFilterQueries(parseQuery(query));
}

function parseQueryClientSide(query: string): UIFilters | Unrepresentable {
  return spreadQueries(parseQuery(query));
}

function getStateFromString(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
): QueryState {
  try {
    // Default UI Filters
    if (queryMode === "UIFilter") {
      const parsedUIFilters = parseQueryClientSide(query);
      if (parsedUIFilters !== "unrepresentable") {
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
          queryPropertyFiltersForceRehydrate: false,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryParameters: parsedUIFilters,
          queryString: sumQueries(parsedUIFilters).toString(),
          queryParseError: undefined,
          isBaselineQuery: false,
        };
      }
    }
    // Property Filters
    if (queryMode === "PropertyFilter") {
      const parsedPropertyFilters = parsePropertyFilterQueryClientSide(query);
      if (parsedPropertyFilters !== "unrepresentable") {
        return {
          unrepresentable: false,
          queryMode: queryMode,
          queryParameters: {},
          queryPropertyFilters: parsedPropertyFilters,
          queryPropertyFiltersForceRehydrate: true,
          activePropertyFilters: {},
          activePropertyFiltersVisibilityEnabled: false,
          queryString: sumPropertyFilterQueries(
            parsedPropertyFilters
          ).toString(),
          queryParseError: undefined,
          isBaselineQuery: false,
        };
      }
    }
    // Unrepresentable Filters
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: undefined,
      isBaselineQuery: false,
    };
  } catch (e: any) {
    return {
      unrepresentable: true,
      queryMode: queryMode,
      queryPropertyFilters: DEFAULT_PROPERTY_FILTER_GROUP_VALUE,
      queryPropertyFiltersForceRehydrate: false,
      activePropertyFilters: {},
      activePropertyFiltersVisibilityEnabled: false,
      queryParameters: {},
      queryString: query,
      queryParseError: (e as Error).toString(),
      isBaselineQuery: false,
    };
  }
}

function getStartingQuery(): QueryState {
  if (typeof window === "object") {
    const queryFromUrl = window.location.href.split("#")[1];
    if (queryFromUrl !== undefined && queryFromUrl.trim() !== "") {
      const queryUri = decodeURI(queryFromUrl.split("(queryMode)")[0]);
      const queryMode = queryFromUrl.includes("(queryMode)=PropertyFilter")
        ? "PropertyFilter"
        : "UIFilter";
      return getStateFromString(queryUri, queryMode);
    }
  }
  return defaultQueryState;
}

function writeQueryToUrl(
  query: string,
  queryMode: "UIFilter" | "PropertyFilter" = "UIFilter"
) {
  //budget routing
  if (typeof window === "object") {
    const domain = getDomainFromUrl();
    window.location.replace(`${domain}#${query}(queryMode)=${queryMode}`);
  }
}

// Sanitize input text to match with expected search.ts valueTupleMatcher
// This helps to reduce 500 errors when characters are not allowed by valueTupleMatcher
const sanitizeInputFilters = (value: string): string => {
  if (!value || value === "") return "";
  // Replace all characters that don't match the allowed pattern
  return value.replace(/[^\w\s\-.,:+\/|]/g, "-");
};

type QueryState = {
  unrepresentable: boolean;
  queryMode: "PropertyFilter" | "UIFilter";
  queryPropertyFilters: PropertyFilterGroup;
  queryPropertyFiltersForceRehydrate: boolean;
  activePropertyFilters: { [key: string]: boolean };
  activePropertyFiltersVisibilityEnabled: boolean;
  queryParameters: UIFilters;
  queryString: string;
  pendingQueryString?: string;
  queryParseError?: string;
  isBaselineQuery: boolean;
};

const generateQuery = createSlice({
  name: "queryParameters",
  initialState: getStartingQuery(),
  reducers: {
    resetFilter(state) {
      state.unrepresentable = false;
      state.queryParameters = {};
      state.queryPropertyFilters = DEFAULT_PROPERTY_FILTER_GROUP_VALUE;
      state.queryPropertyFiltersForceRehydrate = true;
      state.activePropertyFilters = {};
      state.activePropertyFiltersVisibilityEnabled = false;
      state.queryString = "(all)";
      state.pendingQueryString = undefined;
      state.queryParseError = undefined;
      state.isBaselineQuery = false;
    },
    setQueryMode(state, action) {
      state.queryMode = action.payload;
    },
    loadFromUrl(state) {
      const query = getStartingQuery();
      state.isBaselineQuery = query.isBaselineQuery;
      state.unrepresentable = query.unrepresentable;
      state.queryMode = query.queryMode;
      state.queryPropertyFilters = query.queryPropertyFilters;
      state.queryPropertyFiltersForceRehydrate =
        query.queryPropertyFiltersForceRehydrate;
      state.activePropertyFilters = query.activePropertyFilters;
      state.activePropertyFiltersVisibilityEnabled =
        query.activePropertyFiltersVisibilityEnabled;
      state.queryParameters = query.queryParameters;
      state.queryString = query.queryString;
      state.queryParseError = query.queryParseError;
    },
    setAttributeQuery(state, action) {
      if (action.payload.queryState !== undefined) {
        state.queryParameters = {
          ...(state.unrepresentable ? {} : state.queryParameters),
          [action.payload.attribute]: action.payload.queryState,
        };
        state.unrepresentable = false;
      }
    },
    setQueryPropertyFilters(state, action) {
      state.queryPropertyFilters = action.payload;
      state.unrepresentable = false;
    },
    setQueryPropertyFiltersForceRehydrate(state, action) {
      state.queryPropertyFiltersForceRehydrate = action.payload;
    },
    resetActivePropertyFilters(state) {
      state.activePropertyFilters = {};
    },
    setActivePropertyFilter(state, action) {
      state.activePropertyFilters = {
        ...state.activePropertyFilters,
        [action.payload]: true,
      };
    },
    setActivePropertyFiltersVisibilityEnabled(state, action) {
      state.activePropertyFiltersVisibilityEnabled = action.payload;
    },
    setBaselineAttributeQuery(state, action) {
      if (action.payload.queryState !== undefined) {
        if (state.queryMode === "UIFilter") {
          state.queryParameters = {
            ...(state.unrepresentable ? {} : state.queryParameters),
            [action.payload.attribute]: action.payload.queryState,
          };
        }
        if (state.queryMode === "PropertyFilter") {
          // queryPropertyFilters contains at most two children an ASIN PropertyFilter and
          // a PropertyFilterGroup with the rest of the attribute filters
          const groupId = state.queryPropertyFilters?.filters?.findIndex(
            (f: PropertyFilter | PropertyFilterGroup) =>
              f.type === "PropertyFilterGroup"
          );
          let filterGroup =
            groupId >= 0
              ? state.queryPropertyFilters?.filters[groupId]
              : { ...DEFAULT_PROPERTY_FILTER_GROUP_VALUE };
          // Remove previous baselineStatus if exist
          filterGroup = removePropertyFilter(
            action.payload.attribute,
            filterGroup as PropertyFilterGroup
          );
          filterGroup.filters.push({
            type: "PropertyFilterGroup",
            operation: "or",
            filters: (action.payload.queryState.value ?? "").split(",").map(
              (value: string): PropertyFilter => ({
                type: "PropertyFilter",
                operator: "filter",
                propertyKey: action.payload.attribute,
                value: value,
              })
            ),
          });
          if (groupId >= 0) {
            state.queryPropertyFilters.filters[groupId] = filterGroup;
          } else {
            state.queryPropertyFilters.filters.push(filterGroup);
          }
        }
        state.unrepresentable = false;
        state.queryPropertyFiltersForceRehydrate = true;
        state.isBaselineQuery = true;
      }
    },
    unsetAttributeQuery(state, action) {
      if (action.payload.attribute !== undefined) {
        delete state.queryParameters[action.payload.attribute];
      }
    },
    setQueryString(state) {
      if (
        state.pendingQueryString !== undefined &&
        state.pendingQueryString !== ""
      ) {
        const newState = getStateFromString(
          state.pendingQueryString,
          state.queryMode
        );
        writeQueryToUrl(newState.queryString, state.queryMode);
        Object.assign(state, newState);
      } else if (!state.unrepresentable) {
        if (state.queryMode === "UIFilter") {
          const queryString = sumQueries(state.queryParameters).toString();
          writeQueryToUrl(queryString);
          state.queryString = queryString;
        }
        if (state.queryMode === "PropertyFilter") {
          const queryString = sumPropertyFilterQueries(
            state.queryPropertyFilters
          ).toString();
          writeQueryToUrl(queryString, "PropertyFilter");
          state.queryString = queryString;
        }
      }
    },
    setPendingQueryString(state, action) {
      state.pendingQueryString = action.payload;
    },
  },
});

export const {
  setQueryMode,
  setQueryPropertyFilters,
  setQueryPropertyFiltersForceRehydrate,
  setActivePropertyFilter,
  setActivePropertyFiltersVisibilityEnabled,
  resetActivePropertyFilters,
  resetFilter,
  setAttributeQuery,
  unsetAttributeQuery,
  setQueryString,
  setPendingQueryString,
  setBaselineAttributeQuery,
  loadFromUrl,
} = generateQuery.actions;

export default generateQuery.reducer;
